import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Hostel } from '@/api/types';
import { SignOnBehalfFields, EMPTY_SIGN_ON_BEHALF, type SignOnBehalfValue } from '@/components/SignatureCapture';

/**
 * Standalone action for an already-admitted tenant who hasn't signed yet — lets an
 * owner/supervisor record the in-person acknowledgment from their own screen.
 */
export function SignTermsOnBehalfInline({ tenantId, hostel, onDone }: {
  tenantId: string; hostel: Hostel | null; onDone: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState<SignOnBehalfValue>(EMPTY_SIGN_ON_BEHALF);

  const mutation = useMutation({
    mutationFn: () => client.post(`/api/tenants/${tenantId}/accept-terms`, { signature: value.signature }),
    onSuccess: onDone,
  });

  return (
    <div className="space-y-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
      <SignOnBehalfFields hostel={hostel} value={value} onChange={setValue} forceOpen />
      {mutation.isError && (
        <p className="text-red-600 text-xs">{(mutation.error as Error).message}</p>
      )}
      <button
        type="button"
        disabled={!value.agreed || !value.signature || mutation.isPending}
        onClick={() => mutation.mutate()}
        className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-medium py-2 rounded-lg text-sm transition"
      >
        {mutation.isPending ? t('tenant.saving', 'Saving…') : t('tenant.signOnBehalfSubmit', 'Confirm Signature')}
      </button>
    </div>
  );
}
